import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useSearch } from "@tanstack/react-router";
import { Tag } from "antd";
import {
    useGetOutgoingPostbackLogsExportMutation,
    useGetOutgoingPostbackLogsQuery,
} from "api/TenantSlug/OutgoingPostbackLog/hooks";
import { OutgoingPostbackLogDetailDrawer } from "components/Drawers/OutgoingPostbackLogDetailDrawer/OutgoingPostbackLogDetailDrawer";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { TableColumn } from "components/UI/Table/columns/TableColumn.tsx";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { StepLogMessageCell } from "components/UI/Table/UI/StepLogMessageCell/StepLogMessageCell";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useRowHandlers } from "hooks/useTableRowHandlers";
import { Key } from "react";
import { useRecoilValue } from "recoil";
import { PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import GetOutgoingPostbackLogsResponse = App.Http.Responses.OutgoingPostbackLog.GetOutgoingPostbackLogsResponse;
import OutgoingPostbackLogData = App.Data.OutgoingPostbackLogData;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

interface SearchParams {
    [`outgoingPostbackLogDetail-id`]: string;
}

export const OutgoingPostbackLogTable = () => {
    const tableId = TableId.OutgoingPostbackLogTable;
    const { t } = usePaldockTranslation("tables.outgoing-postback-log");
    const { t: tEnum } = usePaldockTranslation("general");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");
    const tableAtomInstance = tableAtom<OutgoingPostbackLogData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const exportState = useRecoilValue(exportAtom(tableId));
    const permissionsUtils = usePermission();
    const canViewOutput = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackLogViewOutput);
    const canViewAdvertiser = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackLogViewAdvertiser);
    const createdAtStr = useCreatedAtFilterValue();
    const searchParams = useSearch({ strict: false }) as SearchParams;

    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                ...(tableState.filter?.filter ?? {}),
                ...(tableState.filter?.filter?.created_at
                    ? {}
                    : {
                          created_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            include:
                tableState.filter?.include ??
                [
                    "outgoingPostback",
                    "outgoingPostback.owner",
                    "conversionState",
                    "conversionState.owner",
                    canViewAdvertiser ? "conversionState.advertiser" : null,
                    "conversionState.offer",
                ]
                    .filter(Boolean)
                    .join(","),
            sort: tableState.filter?.sort ?? "-created_at",
        },
    };

    const getOutgoingPostbackLogsQuery = useGetOutgoingPostbackLogsQuery(request, { enabled: false });
    const getOutgoingPostbackLogsExportMutation = useGetOutgoingPostbackLogsExportMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetOutgoingPostbackLogsResponse,
        void,
        OutgoingPostbackLogData,
        BaseExportRequest
    >(tableId, getOutgoingPostbackLogsQuery, undefined, undefined, getOutgoingPostbackLogsExportMutation);

    const { getRowHandlers, getRowClassName } = useRowHandlers<OutgoingPostbackLogData>({
        actions: [
            {
                type: "drawer",
                drawerId: DrawerId.OutgoingPostbackLogDetail,
                drawerParam: `${DrawerId.OutgoingPostbackLogDetail}-id`,
                permission: PermissionEnum.OutgoingPostbackLogView,
            },
        ],
    });

    const columnConfig: TableColumnType<OutgoingPostbackLogData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("date")} />,
            dataIndex: "created_at",
            key: "created_at",
            render: (v) => <DateTimeCell value={v} />,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("conversion_id")} />,
            dataIndex: "conversionState.origin_eid",
            key: "conversionState.origin_eid",
            render: (_, row) =>
                row.conversionState?.origin_eid ? (
                    <ShortenedIdWithCopy id={row.conversionState.origin_eid} />
                ) : (
                    <EmptyCell />
                ),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "conversionState.origin_eid",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("event_id")} />,
            dataIndex: "conversionState.eid",
            key: "conversionState.eid",
            render: (_, row) =>
                row.conversionState?.eid ? <ShortenedIdWithCopy id={row.conversionState.eid} /> : <EmptyCell />,
            sorter: false,
            filterable: true,
            filterType: FILTER_TYPES.eid,
            filterName: "conversionState.eid",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("external_id")} />,
            dataIndex: "advertiser_custom_id",
            key: "advertiser_custom_id",
            render: (v) => (v ? <ShortenedIdWithCopy id={v} /> : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "advertiser_custom_id",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("transaction")} />,
            dataIndex: "conversionState.type",
            key: "conversionState.type",
            render: (_, row) => row.conversionState?.type ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.conversionStateTypeSelect,
            filterName: "conversionState.type",
            ellipsis: true,
            width: 120,
        },
        TableColumn.Entity.Owner<OutgoingPostbackLogData>({
            title: <ColumnHeaderTitle columnTitle={tG("affiliate")} />,
            dataIndex: "conversionState.owner.nickname",
            key: "conversionState.owner.nickname",
            filterName: "conversionState.owner_id",
            sortName: "conversionState.owner.nickname",
            ellipsis: true,
            width: 150,
            getEntity: (row) => row.conversionState?.owner,
        }),
        ...(canViewAdvertiser
            ? [
                  TableColumn.Entity.Advertiser<OutgoingPostbackLogData>({
                      title: <ColumnHeaderTitle columnTitle={tG("advertiser")} />,
                      dataIndex: "conversionState.advertiser.nickname",
                      key: "conversionState.advertiser.nickname",
                      filterType: FILTER_TYPES.advertiserSelect,
                      filterName: "conversionState.advertiser_id",
                      sortName: "conversionState.advertiser.nickname",
                      ellipsis: true,
                      width: 150,
                      getEntity: (row) => row.conversionState?.advertiser,
                  }),
              ]
            : []),
        TableColumn.Entity.Offer<OutgoingPostbackLogData>({
            title: <ColumnHeaderTitle columnTitle={tG("offer")} />,
            dataIndex: "conversionState.offer.title",
            key: "conversionState.offer.title",
            sortName: "conversionState.offer.title",
            filterName: "conversionState.offer_id",
            ellipsis: true,
            width: 150,
            getEntity: (row) => row.conversionState?.offer,
        }),
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "outgoingPostback.name",
            key: "outgoingPostback.name",
            render: (_, row) => <EntityLinkCell.OutgoingPostback entity={row.outgoingPostback} />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "outgoingPostback.name",
            ellipsis: true,
            width: 150,
        },
        TableColumn.Entity.Owner<OutgoingPostbackLogData>({
            title: <ColumnHeaderTitle columnTitle={t("columns.owner")} />,
            dataIndex: "outgoingPostback.owner.nickname",
            key: "outgoingPostback.owner.nickname",
            filterName: "outgoingPostback.owner_id",
            sortName: "outgoingPostback.owner.nickname",
            ellipsis: true,
            width: 150,
            getEntity: (row) => row.outgoingPostback?.owner,
        }),
        {
            title: <ColumnHeaderTitle columnTitle={tG("state")} />,
            dataIndex: "state",
            key: "state",
            render: (state) =>
                state ? (
                    <Tag icon={<CheckCircleOutlined />} color="green">
                        {tGL("ok")}
                    </Tag>
                ) : (
                    <Tag icon={<CloseCircleOutlined />} color="red">
                        {tGL("fail")}
                    </Tag>
                ),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "state",
            filterOptions: [
                { label: tGL("ok"), value: true },
                { label: tGL("fail"), value: false },
            ],
            ellipsis: true,
            width: 90,
        },
        ...(canViewOutput
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.output")} />,
                      dataIndex: "output",
                      key: "output",
                      render: (v: string) => (v ? tEnum(`postback_log_output.${v}`) : <EmptyCell />),
                      sorter: true,
                      filterable: true,
                      filterType: FILTER_TYPES.string,
                      filterName: "output",
                      ellipsis: true,
                      width: 150,
                  } as TableColumnType<OutgoingPostbackLogData>,
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.detail")} />,
                      dataIndex: "message",
                      key: "message",
                      render: (_: unknown, row: OutgoingPostbackLogData) => (
                          <StepLogMessageCell message={row.message} id={`message-${row.id}`} />
                      ),
                      width: 390,
                      ellipsis: true,
                  } as TableColumnType<OutgoingPostbackLogData>,
              ]
            : []),
        {
            title: <ColumnHeaderTitle columnTitle={tG("result")} />,
            dataIndex: "data.result",
            key: "data.result",
            render: (_, row) => (row.data as any)?.result ?? <EmptyCell />,
            ellipsis: true,
            width: 120,
            visible: false,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("value")} />,
            dataIndex: "data.value",
            key: "data.value",
            render: (_, row) => (row.data as any)?.value ?? <EmptyCell />,
            ellipsis: true,
            width: 100,
            visible: false,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("commission")} />,
            dataIndex: "data.commission",
            key: "data.commission",
            render: (_, row) => (row.data as any)?.commission ?? <EmptyCell />,
            ellipsis: true,
            width: 120,
            visible: false,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("currency")} />,
            dataIndex: "data.currency",
            key: "data.currency",
            render: (_, row) => (row.data as any)?.currency ?? <EmptyCell />,
            ellipsis: true,
            width: 100,
            visible: false,
        },
        ...([1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const).map((n) => ({
            title: <ColumnHeaderTitle columnTitle={tG(`advs${n}`)} />,
            dataIndex: `data.advs${n}`,
            key: `data.advs${n}`,
            render: (_: unknown, row: OutgoingPostbackLogData) => (row.data as any)?.[`advs${n}`] ?? <EmptyCell />,
            ellipsis: true,
            width: 100,
            visible: false,
        })),
    ];

    return (
        <>
            <Table<GetOutgoingPostbackLogsResponse, OutgoingPostbackLogData>
                tableId={tableId}
                columnConfig={columnConfig}
                data={tableFetchDataUtils.tableData as GetOutgoingPostbackLogsResponse}
                dataSource={tableFetchDataUtils.tableData?.outgoingPostbackLogs ?? []}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                rowKey={(record) => record.id as Key}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                loading={tableFetchDataUtils.fetchQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                enableFilters={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilter)}
                enableColumnsVisibility={permissionsUtils.hasPermission(PermissionEnum.LogColumnSetting)}
                enableTimeFilter={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilterTime)}
                dateFilterDataIndex={"created_at"}
                dateFilterMode={"minute"}
                autoScrollWidth={true}
                hideActionsColumn={true}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page: request.page,
                        filter: request.filter,
                        delimiter: exportState.delimiter,
                    })
                }
            />

            {permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackLogView) &&
                searchParams[`${DrawerId.OutgoingPostbackLogDetail}-id`] && (
                    <OutgoingPostbackLogDetailDrawer logId={searchParams[`${DrawerId.OutgoingPostbackLogDetail}-id`]} />
                )}
        </>
    );
};
